import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Bell, Users } from "lucide-react";
import { Modal } from "@/components/FindPeople";

const ring = "relative inline-flex h-10 w-10 flex-shrink-0 md:w-auto md:gap-2 md:px-4 md:text-sm md:font-medium items-center justify-center rounded-full border transition-colors";

function Count({ n }) {
  if (!(n > 0)) return null;
  return <span className="absolute -right-1 -top-1 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-[#9E1B28] px-1 text-[11px] font-bold leading-none text-amber-50" data-testid="badge-count">{n > 9 ? "9+" : n}</span>;
}

/** The Friends button next to the tabs. Shows how many friend requests are waiting (the requests banner tells us). */
export function FriendsButton({ active, onClick }) {
  const [waiting, setWaiting] = useState(0);
  useEffect(() => {
    const on = (e) => { if (e.detail && Array.isArray(e.detail.incoming)) setWaiting(e.detail.incoming.length); };
    window.addEventListener("mytheatre:friends", on);
    let live = true;
    api.get("/friends").then((r) => { if (live && r.data.has_username !== false) setWaiting((r.data.incoming || []).length); }).catch(() => {});   // the requests banner keeps it fresh after this
    return () => { live = false; window.removeEventListener("mytheatre:friends", on); };
  }, []);
  return (
    <button type="button" onClick={onClick} aria-label={waiting ? `Friends, ${waiting} request${waiting > 1 ? "s" : ""} waiting` : "Friends"} title="Friends" data-testid="open-friends"
      className={`${ring} ${active ? "border-[#C89B3C] bg-[#C89B3C] text-black" : "border-border bg-card text-muted-foreground hover:text-foreground"}`}>
      <Users className="h-4 w-4" /><span className="hidden md:inline">Friends</span><Count n={waiting} />
    </button>
  );
}

/** The Activity bell: who liked, reacted to or commented on what you shared. Opens a pop-up and marks everything as read. */
export default function ActivityBell() {
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);

  const count = useCallback(() => { api.get("/notifications").then((r) => setUnread(r.data.unread || 0)).catch(() => {}); }, []);
  useEffect(() => {
    count();
    const again = () => { if (document.visibilityState !== "hidden") count(); };
    const id = setInterval(again, 45000);
    window.addEventListener("focus", again);
    document.addEventListener("visibilitychange", again);
    return () => { clearInterval(id); window.removeEventListener("focus", again); document.removeEventListener("visibilitychange", again); };
  }, [count]);

  const show = async () => {
    setOpen(true); setItems(null);
    try {
      const r = await api.get("/notifications");
      setItems(r.data.items || []);
      if ((r.data.unread || 0) > 0) { setUnread(0); api.post("/notifications/read").catch(() => {}); }
    } catch { setItems([]); }
  };

  return (
    <>
      <button type="button" onClick={show} aria-label={unread ? `Activity, ${unread} new` : "Activity"} title="Activity" data-testid="open-activity"
        className={`${ring} border-border bg-card text-muted-foreground hover:text-foreground`}>
        <Bell className="h-4 w-4" /><span className="hidden md:inline">Activity</span><Count n={unread} />
      </button>
      {open && (
        <Modal title="Activity" icon={Bell} onClose={() => setOpen(false)} testid="activity">
          {!items ? <p className="mt-4 text-sm text-muted-foreground">Loading…</p> : items.length === 0 ? (
            <p className="mt-4 text-sm text-muted-foreground" data-testid="activity-empty">Nothing new. When friends like, react to or comment on what you share, you'll see it here.</p>
          ) : (
            <ul className="mt-4 space-y-2" data-testid="activity-list">
              {items.map((n) => (
                <li key={n.id} data-testid="activity-item" className={`rounded-xl border px-3 py-2.5 text-sm ${n.read ? "border-border text-muted-foreground" : "border-[#C89B3C]/40 bg-[#C89B3C]/10 font-medium text-foreground"}`}>{n.text}</li>
              ))}
            </ul>
          )}
        </Modal>
      )}
    </>
  );
}